'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useT } from '../../lib/i18n';
import { plainText, type ReviewRow, type ReviewSort, type ReviewStats } from '../../lib/reviews-shared';
import { Icon } from '../Icon';
import { HalfStarDef, Stars, day } from './bits';
import { SmartLink } from '../SmartLink';
import { BoardPager, BoardSearch } from '../boards/BoardBits';

/**
 * 수강후기 목록 — 가이드 `review-list.html` 그대로(앨범형·리스트형 두 벌 + 뷰 토글).
 *
 * **처음 어느 뷰로 열지는 마켓이 정한다**(설정 > 기본설정 > 수강후기 리스트, 2026-09-05).
 * 회원이 토글로 바꾸면 **그 브라우저에서는 그 뒤로 회원이 고른 값**을 쓴다 — 목록을 볼
 * 때마다 되돌아가면 토글이 아니다. 강좌 목록의 뷰 기억과 같은 결이다.
 *
 * **베스트는 어느 뷰에서든 맨 위 띠**(슬라이더)다(2026-09-05 사용자 지시). 리스트형에서는
 * 가이드 앨범형 슬라이더를 쓰되 **썸네일만 뺀 카드**를 태운다 — 강의 후기에는 사진이 없다.
 * 베스트의 정체는 **관리자가 올린 공지**(`isNotice`)다. 별도 칸을 만들지 않았다.
 *
 * 정렬·쪽은 **주소에 담는다**(게시판과 같은 규칙) — 뒤로 가기와 공유가 그대로 된다.
 * 뷰만 화면 상태다(주소에 담으면 공유한 사람의 취향까지 따라간다).
 */
/**
 * 회원이 고른 뷰 — **쿠키**에 담는다(2026-09-05).
 * localStorage 로 하면 서버는 마켓 기본값으로 그리고 브라우저가 뒤늦게 바꿔 **화면이 한 번
 * 번쩍인다**(그리고 그 되돌림은 효과 안에서 상태를 바꾸는 꼴이라 렌더가 한 번 더 돈다).
 * 쿠키는 서버도 읽으므로 **처음부터 회원이 고른 뷰로** 그린다. 말 설정과 같은 방식이다.
 */
const VIEW_COOKIE = 'sp_review_view';
const YEAR = 60 * 60 * 24 * 365;

export function ReviewList({
  rows, best, total, page, size, stats, sort, base, canWrite, defaultView,
  onSort, onPage, hideSearch = false, loading = false, backTo,
}: {
  rows: ReviewRow[];
  best: ReviewRow[];
  total: number; page: number; size: number;
  stats: ReviewStats;
  sort: ReviewSort;
  base: string;
  /** 로그인했나 — 후기는 들은 강좌에만 쓸 수 있어 자격은 쓰기 화면이 다시 본다 */
  canWrite: boolean;
  /** 마켓이 정한 기본 뷰 */
  defaultView: 'card' | 'list';
  /**
   * 정렬·쪽을 **어떻게 바꾸나** — 안 주면 주소를 바꾼다(후기 목록 화면. 뒤로 가기·공유가 된다).
   * 주면 그 함수를 부른다 — **강좌 상세의 후기 탭**이 그렇다. 거기서 주소를 바꾸면 탭이
   * 처음으로 돌아가고 상세가 통째로 다시 그려진다.
   */
  onSort?: (s: ReviewSort) => void;
  onPage?: (n: number) => void;
  /** 검색줄을 감춘다 — 한 강좌 안에서는 찾을 것이 몇 줄 안 된다(강좌 상세) */
  hideSearch?: boolean;
  /** 받아 오는 중 — 목록 자리에 뼈대를 그린다(강좌 상세는 탭을 열 때 받는다) */
  loading?: boolean;
  /**
   * 후기 보기에서 **[목록으로] 가 돌아갈 자리**(2026-09-07 사용자 제보).
   * 강좌 상세에서 들어왔는데 게시판 목록으로 튕기면 보던 자리를 잃는다.
   * 값이 있으면 줄 링크에 실어 보낸다 — 보기 화면이 그 주소를 그대로 쓴다.
   */
  backTo?: string;
}) {
  const t = useT();
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  /** 서버가 이미 정해서 넘겨 준 뷰(쿠키 > 마켓 기본값) — 여기서 다시 판단하지 않는다 */
  const [view, setView] = useState<'card' | 'list'>(defaultView);

  const pickView = (v: 'card' | 'list') => {
    setView(v);
    document.cookie = `${VIEW_COOKIE}=${v}; path=/; max-age=${YEAR}; samesite=lax`;
  };

  const goSort = (v: ReviewSort) => {
    if (onSort) { onSort(v); return; }
    const q = new URLSearchParams(params.toString());
    if (v === 'new') q.delete('sort'); else q.set('sort', v);
    q.delete('page'); // 정렬이 바뀌면 첫 쪽부터다
    router.push(`${pathname}${q.size ? `?${q}` : ''}`);
  };

  const bars: Array<[label: string, pct: number]> = [
    ['5', stats.dist.r5], ['4', stats.dist.r4], ['3', stats.dist.r3], ['2', stats.dist.r2], ['1', stats.dist.r1],
  ];

  return (
    <>
      <HalfStarDef />

      {/* 별점 요약 — 평균·개수·분포. 값은 서버가 센 것을 그대로 쓴다 */}
      <div className="rv-summary mb20">
        <div className="rv-summary-score">
          <span className="rv-score-big">{stats.avg.toFixed(1)}</span>
          <div>
            <Stars score={stats.avg} />
            <p className="rv-score-cnt">{t('rv.totalCount', { n: stats.count.toLocaleString() })}</p>
          </div>
        </div>
        <div className="rv-score-bars">
          {bars.map(([label, pct]) => (
            <div className="rv-bar-row" key={label}>
              <span className="rv-bar-label">{t('rv.nStar', { n: label })}</span>
              <div className="rv-bar-bg"><div className="rv-bar-fill" style={{ width: `${pct}%` }} /></div>
              <span className="rv-bar-pct">{pct}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="sort-wrap mb15">
        <div className="d-flex ai-center gap10">
          <select className="guide-select guide-select-sm" value={sort}
            onChange={(e) => goSort(e.target.value as ReviewSort)}>
            <option value="new">{t('rv.sort.new')}</option>
            <option value="high">{t('rv.sort.high')}</option>
            <option value="low">{t('rv.sort.low')}</option>
          </select>
        </div>
        {/* 뷰 토글 — 가이드 `view-toggle`. 고른 값은 이 브라우저에 남는다 */}
        <div className="view-toggle">
          <button type="button" className={`view-btn${view === 'card' ? ' active' : ''}`}
            title={t('rv.view.card')} aria-label={t('rv.view.card')} onClick={() => pickView('card')}>
            <Icon name="layout-grid" />
          </button>
          <button type="button" className={`view-btn${view === 'list' ? ' active' : ''}`}
            title={t('rv.view.list')} aria-label={t('rv.view.list')} onClick={() => pickView('list')}>
            <Icon name="list" />
          </button>
        </div>
      </div>

      {/* 검색 — 가이드는 요약 아래 한 줄(제목·본문·강좌 이름에서 찾는다) */}
      {!hideSearch && <BoardSearch placeholder={t('rv.searchPh')} />}

      {/* 베스트 — 어느 뷰에서든 맨 위 띠. 리스트형에서는 썸네일 없는 카드가 실린다 */}
      {best.length > 0 && (
        <>
          <SectionTitle label={t('rv.best')} write={canWrite ? `${base}/write` : null} />
          <BestSlider rows={best} base={base} withThumb={view === 'card'} backTo={backTo} />
        </>
      )}

      {/*
        [후기 쓰기] 는 **한 번만** 낸다 — 가이드는 베스트 줄 오른쪽에 두는데, 베스트가 하나도
        없는 마켓에서는 그 줄이 아예 안 그려진다. 그때는 전체 후기 줄이 그 자리를 받는다.
      */}
      <SectionTitle label={t('rv.all')} write={canWrite && best.length === 0 ? `${base}/write` : null} />

      {loading ? (
        <div className="list-sentinel"><span className="sk sk-line" style={{ width: 160, margin: '0 auto' }} /></div>
      ) : rows.length === 0 ? (
        <div className="cp-empty"><p>{t('rv.empty')}</p></div>
      ) : view === 'card' ? (
        <div className="rv-grid mb20">
          {rows.map((r) => <Card key={r.id} r={r} base={base} withThumb backTo={backTo} />)}
        </div>
      ) : (
        <div className="rv-list mb20">
          {rows.map((r) => <ListItem key={r.id} r={r} base={base} backTo={backTo} />)}
        </div>
      )}

      {/* 쪽 — 주소로 옮기는 것이 기본, 콜백을 주면 그 자리에서 바꾼다(강좌 상세) */}
      {onPage
        ? <EmbeddedPager page={page} total={total} size={size} onPage={onPage} />
        : <BoardPager page={page} total={total} size={size} />}
    </>
  );
}

/**
 * 주소를 바꾸지 않는 페이저 — 강좌 상세처럼 **그 자리에서** 쪽을 넘길 때 쓴다.
 * 모양은 게시판 페이저와 같다(`pagination`·`page-btn`) — 두 곳이 달라 보이면 안 된다.
 */
function EmbeddedPager({ page, total, size, onPage }: {
  page: number; total: number; size: number; onPage: (n: number) => void;
}) {
  const t = useT();
  const pages = Math.max(1, Math.ceil(total / size));
  if (!total) return null;
  const start = Math.max(1, Math.min(page - 2, pages - 4));
  const nums = Array.from({ length: Math.min(5, pages - start + 1) }, (_, i) => start + i);
  return (
    <div className="d-flex jc-center my20">
      <div className="pagination">
        <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label={t('bbs.prevPage')}>
          <Icon name="chevron-left" />
        </button>
        {nums.map((n) => (
          <button key={n} className={`page-btn${n === page ? ' active' : ''}`} onClick={() => onPage(n)}>{n}</button>
        ))}
        <button className="page-btn" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label={t('bbs.nextPage')}>
          <Icon name="chevron-right" />
        </button>
      </div>
    </div>
  );
}

/** 섹션 제목 줄 — 가이드 `rv-section-title`(오른쪽 끝에 [후기 쓰기]) */
function SectionTitle({ label, write }: { label: string; write: string | null }) {
  const t = useT();
  return (
    <div className="rv-section-title mb10">
      <span className="lec-sec-bar"></span>
      <span className="fs14 fw-bold">{label}</span>
      {write && (
        <SmartLink href={write} className="action-btn action-submit btn-sm" style={{ marginLeft: 'auto' }}>
          <Icon name="pen-line" /> {t('rv.write')}
        </SmartLink>
      )}
    </div>
  );
}

/**
 * 후기 보기 주소 — 돌아갈 자리가 있으면 실어 보낸다.
 * **내부 경로만** 싣는다(`/` 로 시작, `//` 는 아님) — 바깥 주소가 섞이면 그게 곧 열린 리디렉션이다.
 */
function reviewHref(base: string, id: number, backTo?: string): string {
  const ok = backTo && backTo.startsWith('/') && !backTo.startsWith('//');
  return ok ? `${base}/${id}?from=${encodeURIComponent(backTo)}` : `${base}/${id}`;
}

/** 앨범형 한 장 — 가이드 `rv-card`. 리스트형 베스트 띠에서는 썸네일 없이 몸통만 쓴다 */
function Card({ r, base, withThumb, best, backTo }: { r: ReviewRow; base: string; withThumb: boolean; best?: boolean; backTo?: string }) {
  const t = useT();
  return (
    <SmartLink href={reviewHref(base, r.id, backTo)} className={`rv-card${best ? ' best' : ''}`}>
      {withThumb && (
        <div className="rv-card-thumb rv-thumb rv-thumb-16x9">
          {/* 첫 사진이 썸네일 — 없으면 가이드대로 빈 그림 표시(`rv-thumb-no-img`) */}
          {r.attachments[0]
            ? <img src={r.attachments[0].url} alt="" loading="lazy" />
            : <div className="rv-thumb-no-img"><Icon name="image" /></div>}
          {best && <span className="badge badge-warning rv-best-badge">{t('rv.best')}</span>}
        </div>
      )}
      <div className="rv-card-body">
        {!withThumb && best && (
          <div className="d-flex ai-center gap5 mb5">
            <span className="badge badge-warning badge-pill">{t('rv.best')}</span>
          </div>
        )}
        <div className="rv-card-stars"><Stars score={r.starRating ?? 0} /></div>
        <p className="rv-card-title">{r.subject || t('rv.noTitle')}</p>
        <p className="rv-card-content">{plainText(r.content)}</p>
        <div className="rv-card-meta">
          <span className="rv-card-course">{r.productName ?? ''}</span>
          <span className="rv-card-author">{r.writerName ?? ''} · {day(r.createdAt)}</span>
        </div>
      </div>
    </SmartLink>
  );
}

/** 리스트형 한 줄 — 가이드 `rv-list-item`. 별점열 · 본문 · 메타 */
function ListItem({ r, base, backTo }: { r: ReviewRow; base: string; backTo?: string }) {
  const t = useT();
  return (
    <SmartLink href={reviewHref(base, r.id, backTo)} className="rv-list-item">
      <div className="rv-list-star-col">
        <Stars score={r.starRating ?? 0} />
        <span className="rv-list-score">{(r.starRating ?? 0).toFixed(1)}</span>
      </div>
      <div className="rv-list-body">
        <div className="d-flex ai-center gap5 mb5">
          <span className="rv-list-course">{r.productName ?? ''}</span>
        </div>
        <p className="rv-list-title">{r.subject || t('rv.noTitle')}</p>
        <p className="rv-list-content">{plainText(r.content)}</p>
      </div>
      <div className="rv-list-meta">
        <span>{r.writerName ?? ''}</span>
        <span>{day(r.createdAt)}</span>
      </div>
    </SmartLink>
  );
}

/**
 * 베스트 띠 — 가이드 `rv-slider-*`(한 화면 4장, 좁아지면 3·2·1장은 CSS 가 정한다).
 * 카드 폭을 CSS 가 정하므로 **재어서** 쓴다. 손가락으로도 넘어간다.
 */
function BestSlider({ rows, base, withThumb, backTo }: { rows: ReviewRow[]; base: string; withThumb: boolean; backTo?: string }) {
  const t = useT();
  const track = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [offset, setOffset] = useState(0);
  const [view, setView] = useState(4);
  const touch = useRef<{ x: number; y: number } | null>(null);

  const measure = useCallback(() => {
    const el = track.current;
    const first = el?.firstElementChild as HTMLElement | null;
    if (!el || !first) return;
    const gap = parseFloat(getComputedStyle(el).gap) || 0;
    const w = first.getBoundingClientRect().width;
    setOffset(w + gap);
    setView(Math.max(1, Math.round((el.getBoundingClientRect().width + gap) / (w + gap))));
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const max = Math.max(0, rows.length - view);
  // 창이 넓어져 다 보이면 index 가 범위를 넘는다 — 효과로 되돌리지 않고 그릴 때 깎는다
  const at = Math.min(index, max);

  const onTouchEnd = (e: React.TouchEvent) => {
    const from = touch.current;
    touch.current = null;
    if (!from) return;
    const dx = e.changedTouches[0].clientX - from.x;
    const dy = e.changedTouches[0].clientY - from.y;
    // 세로 스크롤은 뺏지 않는다 — 가로로 더 많이 움직였을 때만 넘긴다
    if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
    setIndex(dx < 0 ? Math.min(max, at + 1) : Math.max(0, at - 1));
  };

  return (
    <div className="rv-slider-wrap mb20">
      <div className="rv-slider-viewport"
        onTouchStart={(e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
        onTouchEnd={onTouchEnd}>
        <div className="rv-slider-track" ref={track} style={{ transform: `translateX(-${at * offset}px)` }}>
          {rows.map((r) => (
            <div className="rv-slide-item" key={r.id}>
              <Card r={r} base={base} withThumb={withThumb} best backTo={backTo} />
            </div>
          ))}
        </div>
      </div>
      {rows.length > view && (
        <>
          <button type="button" className="rv-slider-btn rv-slider-prev" aria-label={t('action.prev')}
            disabled={at <= 0} onClick={() => setIndex(Math.max(0, at - 1))}>
            <Icon name="chevron-left" />
          </button>
          <button type="button" className="rv-slider-btn rv-slider-next" aria-label={t('action.next')}
            disabled={at >= max} onClick={() => setIndex(Math.min(max, at + 1))}>
            <Icon name="chevron-right" />
          </button>
          <div className="rv-slider-dots">
            {Array.from({ length: max + 1 }, (_, i) => (
              <button key={i} type="button" className={`rv-slider-dot${i === at ? ' active' : ''}`}
                aria-label={t('sub.goSlide', { n: String(i + 1) })} onClick={() => setIndex(i)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
